import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { orgDashboardGradFairsApiIsomorphicFetchDetails } from '#/modules/orgDashboard/gradFairs/api/orgDashboardGradFairsApiFetchDetails';
import type { OrgDashboardGradFairHomeLocation } from '#/modules/orgDashboard/gradFairs/pages/home/OrgDashboardGradFairHomeLocation';
import { orgDashboardGradFairsHomeRoute } from '#/modules/orgDashboard/routing/orgDashboard.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';

import { OrgDashboardGradFairsHomePageLoadable } from './OrgDashboardGradFairsHomePageLoadable';

export const handle = {
  fallbackUrlByLocaleToCurrent: true,
  darkBackground: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps;

export async function loader({
  request,
  context,
  params: { orgId },
}: HorizonAppRouteLoaderArgs<typeof orgDashboardGradFairsHomeRoute>) {
  const [currentGradFair] = await Promise.all([
    orgDashboardGradFairsApiIsomorphicFetchDetails({ request, context })(orgId),
    OrgDashboardGradFairsHomePageLoadable.load(),
  ]);

  const locations = currentGradFair.locations.map((location) => {
    const registered = currentGradFair.registeredLocations.includes(
      location.id,
    );

    const onWaitlist = currentGradFair.waitlistLocations.includes(location.id);

    return {
      ...location,
      registered,
      onWaitlist,
      canRegister:
        !registered && location.vacant && currentGradFair.registrationsLeft > 0,
      canWaitlist: !registered && !onWaitlist && !location.vacant,
    } satisfies OrgDashboardGradFairHomeLocation;
  });

  const checkboxType = (() => {
    const canRegisterLength = locations.filter((l) => l.canRegister).length;

    const canWaitlistLength = locations.filter((l) => l.canWaitlist).length;

    const maxChecks =
      Math.min(currentGradFair.registrationsLeft, canRegisterLength) +
      canWaitlistLength;
    return maxChecks > 1 ? ('checkbox' as const) : ('radio' as const);
  })();

  return {
    locations,
    checkboxType,
    invoices: currentGradFair.invoices,
    currentStage: currentGradFair.currentStage,
    hasStageTwo: currentGradFair.hasStageTwo,
    registrationsLeft: currentGradFair.registrationsLeft,
    fairCredits: currentGradFair.fairCredits,
    pricePerLocation: currentGradFair.pricePerLocation,
  };
}

export default function OrgDashboardGradFairsHomeHorizonRoute({
  loaderData: {
    locations,
    checkboxType,
    invoices,
    currentStage,
    hasStageTwo,
    registrationsLeft,
    fairCredits,
    pricePerLocation,
  },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  return (
    <RouteModule title={i18n('Idealist Grad Fairs')}>
      <OrgDashboardGradFairsHomePageLoadable
        locations={locations}
        checkboxType={checkboxType}
        invoices={invoices}
        currentStage={currentStage}
        hasStageTwo={hasStageTwo}
        registrationsLeft={registrationsLeft}
        fairCredits={fairCredits}
        pricePerLocation={pricePerLocation}
      />
    </RouteModule>
  );
}
